import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';

// Markdown naar veilige HTML (samenvattingen, antwoorden, briefing, chat). De opmaak staat in .prose-jerome
// (styles/index.css). Externe links openen in een nieuw tabblad, en ook een link naar een route van de server (/api/…, zoals de link
// naar een bestand of map in OneDrive sinds 2.2.56 en naar een bestand uit WhatsApp sinds 2.3.5): die verlaat de app (doorsturen naar
// OneDrive, een download, het bestand zelf, of bij een fout de vaste zin met het kenmerk of de stand), en in hetzelfde tabblad verving dat
// de app. Links binnen de app (zoals de voorleeslink /?open=briefing&id=…) openen in hetzelfde.

marked.setOptions({ gfm: true, breaks: true });

type Purifier = ReturnType<typeof DOMPurify>;
let purifier: Purifier | undefined;

function isExternal(href: string): boolean {
  if (!/^https?:\/\//i.test(href)) return false;
  try {
    return typeof location === 'undefined' || new URL(href).origin !== location.origin;
  } catch {
    return true;
  }
}

/** Een route van de server in de eigen app (`/api/…`, ook als volledig adres op de eigen origin): geen scherm van de app. */
function isServerRoute(href: string): boolean {
  try {
    const base = typeof location === 'undefined' ? 'http://app.invalid' : location.origin;
    const url = new URL(href, base);
    return url.origin === base && /^\/api(\/|$)/.test(url.pathname);
  } catch {
    return false;
  }
}

/** Eigen DOMPurify met de linkregel, pas bij het eerste gebruik: zonder DOM (node) heeft DOMPurify geen sanitize of addHook. */
function sanitizer(): Purifier {
  if (purifier) return purifier;
  purifier = DOMPurify();
  if (purifier.isSupported) {
    purifier.addHook('afterSanitizeAttributes', (node) => {
      if (node.tagName !== 'A') return;
      const href = node.getAttribute('href') ?? '';
      if (isExternal(href) || isServerRoute(href)) {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      } else node.removeAttribute('target');
    });
  }
  return purifier;
}

export function renderMarkdown(text: string): string {
  const p = sanitizer();
  // Zonder DOM valt er niets veilig te maken (alleen in tests in node): dan de tekst zelf, ontsnapt.
  if (!p.isSupported) return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return p.sanitize(marked.parse(text, { async: false }), { USE_PROFILES: { html: true } });
}

export function Markdown(props: { text: string; className?: string | undefined }) {
  const html = useMemo(() => renderMarkdown(props.text), [props.text]);
  return <div className={`prose-jerome ${props.className ?? ''}`} dangerouslySetInnerHTML={{ __html: html }} />;
}
